import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { UserPlus, Loader2, Mail, Phone, Baby } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

interface Props {
  children: { id: string; full_name: string; parent_id: string | null }[];
  onSuccess: () => void;
}

function phoneToEmail(phone: string): string {
  const cleaned = phone.replace(/\D/g, "");
  return `${cleaned}@phone.nursery.local`;
}

export default function CreateParentAccountDialog({ children, onSuccess }: Props) {
  const [open, setOpen] = useState(false);
  const { profile, session } = useAuth();
  const [loading, setLoading] = useState(false);
  const [loginMethod, setLoginMethod] = useState<"email" | "phone">("phone");

  const [parentName, setParentName] = useState("");
  const [parentEmail, setParentEmail] = useState("");
  const [parentPhone, setParentPhone] = useState("");
  const [parentPassword, setParentPassword] = useState("");
  const [selectedChildIds, setSelectedChildIds] = useState<string[]>([]);

  // Filter to unlinked children
  const unlinkedChildren = children.filter((c) => !c.parent_id);

  const toggleChild = (id: string) => {
    setSelectedChildIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!profile?.nursery_id || !session?.access_token) return;

    if (!parentName.trim() || !parentPassword) {
      toast.error("يرجى ملء جميع الحقول المطلوبة");
      return;
    }

    const email = loginMethod === "phone" ? phoneToEmail(parentPhone) : parentEmail;
    if (!email) {
      toast.error(loginMethod === "phone" ? "يرجى إدخال رقم الهاتف" : "يرجى إدخال البريد الإلكتروني");
      return;
    }

    setLoading(true);

    try {
      const { data, error } = await supabase.functions.invoke("create-parent-account", {
        body: {
          email,
          password: parentPassword,
          full_name: parentName,
          phone: parentPhone || null,
          nursery_id: profile.nursery_id,
          child_ids: selectedChildIds,
        },
      });

      if (error || data?.error) {
        toast.error("خطأ في إنشاء الحساب: " + (data?.error || error?.message));
        setLoading(false);
        return;
      }

      toast.success("تم إنشاء حساب ولي الأمر بنجاح!");
      setOpen(false);
      resetForm();
      onSuccess();
    } catch (err: any) {
      toast.error("حدث خطأ غير متوقع");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setParentName("");
    setParentEmail("");
    setParentPhone("");
    setParentPassword("");
    setSelectedChildIds([]);
    setLoginMethod("phone");
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) resetForm(); }}>
      <DialogTrigger asChild>
        <Button className="gradient-hero border-0">
          <UserPlus className="w-4 h-4 ml-2" />
          إنشاء حساب ولي أمر
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" dir="rtl">
        <DialogHeader>
          <DialogTitle>إنشاء حساب ولي أمر جديد</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Parent Section */}
          <div className="space-y-3">
            <h3 className="font-semibold text-foreground flex items-center gap-2 text-sm">
              <UserPlus className="w-4 h-4 text-primary" />
              بيانات ولي الأمر
            </h3>
            <div>
              <Label>الاسم الكامل *</Label>
              <Input value={parentName} onChange={(e) => setParentName(e.target.value)} required placeholder="أحمد محمد" />
            </div>

            <div>
              <Label className="mb-2 block">طريقة تسجيل الدخول *</Label>
              <div className="flex gap-2">
                <Button type="button" size="sm" variant={loginMethod === "phone" ? "default" : "outline"} className="flex-1 gap-2" onClick={() => setLoginMethod("phone")}>
                  <Phone className="w-4 h-4" /> رقم الهاتف
                </Button>
                <Button type="button" size="sm" variant={loginMethod === "email" ? "default" : "outline"} className="flex-1 gap-2" onClick={() => setLoginMethod("email")}>
                  <Mail className="w-4 h-4" /> البريد الإلكتروني
                </Button>
              </div>
            </div>

            {loginMethod === "phone" ? (
              <div>
                <Label>رقم الهاتف *</Label>
                <Input value={parentPhone} onChange={(e) => setParentPhone(e.target.value)} required placeholder="01xxxxxxxxx" dir="ltr" type="tel" />
                <p className="text-xs text-muted-foreground mt-1">سيستخدم ولي الأمر رقم الهاتف وكلمة المرور لتسجيل الدخول</p>
              </div>
            ) : (
              <div>
                <Label>البريد الإلكتروني *</Label>
                <Input type="email" value={parentEmail} onChange={(e) => setParentEmail(e.target.value)} required placeholder="parent@email.com" dir="ltr" />
              </div>
            )}

            <div>
              <Label>كلمة المرور *</Label>
              <Input type="password" value={parentPassword} onChange={(e) => setParentPassword(e.target.value)} required minLength={6} placeholder="••••••••" dir="ltr" />
            </div>

            {loginMethod === "email" && (
              <div>
                <Label>رقم الهاتف</Label>
                <Input value={parentPhone} onChange={(e) => setParentPhone(e.target.value)} placeholder="01xxxxxxxxx" dir="ltr" />
              </div>
            )}
          </div>

          <div className="border-t border-border" />

          {/* Link Children Section */}
          <div className="space-y-3">
            <h3 className="font-semibold text-foreground flex items-center gap-2 text-sm">
              <Baby className="w-4 h-4 text-primary" />
              ربط الأطفال بولي الأمر
              {selectedChildIds.length > 0 && (
                <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full">{selectedChildIds.length} محدد</span>
              )}
            </h3>
            {unlinkedChildren.length === 0 ? (
              <p className="text-xs text-muted-foreground bg-muted/50 rounded-lg p-3 text-center">
                جميع الأطفال مرتبطين بحسابات أولياء أمور. يمكنك إضافة أطفال جدد من صفحة الأطفال.
              </p>
            ) : (
              <ScrollArea className="max-h-[200px] border rounded-lg">
                <div className="p-2 space-y-1">
                  {unlinkedChildren.map((child) => (
                    <label
                      key={child.id}
                      className="flex items-center gap-3 p-2 rounded-md hover:bg-muted/50 cursor-pointer transition-colors"
                    >
                      <Checkbox
                        checked={selectedChildIds.includes(child.id)}
                        onCheckedChange={() => toggleChild(child.id)}
                      />
                      <span className="text-sm text-foreground">{child.full_name}</span>
                    </label>
                  ))}
                </div>
              </ScrollArea>
            )}
          </div>

          <Button type="submit" className="w-full gradient-hero border-0" disabled={loading}>
            {loading ? <><Loader2 className="w-4 h-4 animate-spin ml-2" />جاري الإنشاء...</> : "إنشاء الحساب"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
